import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

// Icon-only button with a guaranteed hit area (28/32px) — replaces bare "×"
// glyphs and other naked icon clickables. The accessible name is mandatory.
type Variant = 'ghost' | 'danger';
type Size = 'sm' | 'md';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required — icon-only controls have no visible label. */
  'aria-label': string;
  variant?: Variant;
  size?: Size;
}

// Every icon button carries a visible resting background + border so it reads
// unambiguously as a control (matches Button's ghost/danger resting style) —
// no transparent-until-hover icons.
const VARIANTS: Record<Variant, string> = {
  ghost:
    'bg-paper-2 border border-rule text-ink-3 hover:bg-paper hover:text-ink hover:border-rule-2',
  danger:
    'bg-paper-2 border border-rule text-ink-3 hover:bg-negative-soft hover:text-negative hover:border-negative/30',
};
const SIZES: Record<Size, string> = {
  sm: 'h-7 w-7 text-base',
  md: 'h-8 w-8 text-lg',
};

export function IconButton({
  variant = 'ghost',
  size = 'md',
  className,
  type,
  ...rest
}: Props): JSX.Element {
  return (
    <button
      type={type ?? 'button'}
      {...rest}
      className={cn(
        'inline-flex items-center justify-center shrink-0 rounded leading-none transition select-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    />
  );
}
